import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Snackbar,
  ButtonGroup,
  Alert,
} from "@mui/material";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import SearchBar from "../../components/forum/SearchBar";
import useIsMobile from "../../components/hooks/useIsMobile";
import { useListAdvisorApplications } from "../../services/advisorPipeline";
import {
  ListAdvisorApplicationsRequest_ApplicationFilter,
  AdvisorApplication,
} from "../../protogen/advisor_pipeline_service_pb";
import Loading from "../../components/common/Loading";
import AdvisorApplicationCard from "../../components/pipeline/AdvisorApplicationCard";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { APPLICATION_STATUS_ORDER } from "../../components/pipeline/advisorFlow";

interface HeaderColumnProps {
  title: string;
  sortKey: keyof AdvisorApplication;
  sorts: SortField[];
  setSorts: (s: SortField[]) => void;
  flex: number;
  end?: boolean;
}

interface SortField {
  field: keyof AdvisorApplication;
  asc: boolean;
}

const HeaderColumn = ({
  title,
  sortKey,
  sorts,
  setSorts,
  flex,
  end,
}: HeaderColumnProps) => {
  const currentSortVal = sorts.find((s) => s.field === sortKey);
  const upArrow = currentSortVal && !currentSortVal.asc;

  const onClick = () => {
    const newSorts = sorts.filter((s) => s.field !== sortKey);
    newSorts.unshift({ field: sortKey, asc: !upArrow });
    setSorts(newSorts);
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      flex={flex}
      justifyContent={end ? "end" : "start"}
    >
      <Typography
        component="span"
        variant="bodySmallHeavy"
        sx={{
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={onClick}
      >
        {title}
      </Typography>
    </Box>
  );
};

const applicationStringMatch = (
  application: AdvisorApplication,
  query: string,
) => {
  const searchText = query.toLowerCase();
  return (
    application.firstName.toLowerCase().includes(searchText) ||
    application.lastName.toLowerCase().includes(searchText) ||
    application.email.toLowerCase().includes(searchText) ||
    application.location.toLowerCase().includes(searchText) ||
    application.status.toLowerCase().includes(searchText)
  );
};

const groupApplicationsByStatus = (applications: AdvisorApplication[]) => {
  const grouped: Record<string, AdvisorApplication[]> = {};

  applications.forEach((app) => {
    const status = app.status;
    if (!grouped[status]) {
      grouped[status] = [];
    }
    grouped[status].push(app);
  });

  return grouped;
};

const formatStatusDisplayName = (status: string): string => {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default () => {
  const isMobile = useIsMobile();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filter from URL or default
  const initialFilter =
    searchParams.get("filter") ||
    ListAdvisorApplicationsRequest_ApplicationFilter.NEW;
  const [mainFilter, setMainFilter] =
    useState<ListAdvisorApplicationsRequest_ApplicationFilter>(
      Number(initialFilter) as ListAdvisorApplicationsRequest_ApplicationFilter,
    );
  const [initialApplicationRef, setInitialApplicationRef] = useState<
    string | undefined
  >(params.applicationRef);
  const [sorts, setSorts] = useState<SortField[]>([]);
  const [query, setQuery] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const { request, data, loading } = useListAdvisorApplications();

  const refresh = async () => {
    await request({
      applicationRefs: params.applicationRef ? [params.applicationRef] : [],
      filter: mainFilter,
    });
  };

  // Update URL when filter changes
  useEffect(() => {
    refresh();
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("filter", mainFilter.toString());
      return newParams;
    });
  }, [mainFilter]);

  // Update URL when application card is opened/closed
  const handleApplicationCardOpen = (ref: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("filter", mainFilter.toString());
      return newParams;
    });
    navigate(
      `/tools/advisor-applications/${ref}?filter=${mainFilter.toString()}`,
      { replace: true },
    );
  };

  const handleApplicationCardClose = () => {
    setInitialApplicationRef(undefined);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("filter", mainFilter.toString());
      return newParams;
    });
    navigate(`/tools/advisor-applications?filter=${mainFilter.toString()}`, {
      replace: true,
    });
  };

  let applications = data?.applications || [];
  // On initial load, we want to show the application card for the application that was referenced in the URL at the top.
  applications = [
    ...applications.filter((app) => initialApplicationRef === app.ref),
    ...applications.filter((app) => initialApplicationRef !== app.ref),
  ];

  if (query) {
    applications = applications.filter((app) =>
      applicationStringMatch(app, query),
    );
  }

  const appCards = () => {
    const shouldGroupByStatus =
      mainFilter ===
        ListAdvisorApplicationsRequest_ApplicationFilter.IN_PROGRESS ||
      mainFilter === ListAdvisorApplicationsRequest_ApplicationFilter.ALL;

    if (shouldGroupByStatus) {
      const groupedApplications = groupApplicationsByStatus(applications);

      return APPLICATION_STATUS_ORDER.map((status) => {
        const statusApplications = groupedApplications[status];
        if (!statusApplications || statusApplications.length === 0) {
          return null;
        }

        return (
          <Box
            key={status}
            sx={{
              marginBottom: "8px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="bodyHeavy"
              sx={{ marginBottom: "8px", marginLeft: "8px" }}
            >
              {formatStatusDisplayName(status)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {statusApplications.map((application) => (
                <AdvisorApplicationCard
                  key={application.ref}
                  initialApplication={application}
                  forceOpen={initialApplicationRef === application.ref}
                  onClose={() => handleApplicationCardClose()}
                  onOpen={() => handleApplicationCardOpen(application.ref)}
                />
              ))}
            </Box>
          </Box>
        );
      }).filter(Boolean);
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {applications.map((application) => (
            <AdvisorApplicationCard
              key={application.ref}
              initialApplication={application}
              forceOpen={initialApplicationRef === application.ref}
              onClose={() => handleApplicationCardClose()}
              onOpen={() => handleApplicationCardOpen(application.ref)}
            />
          ))}
        </Box>
      );
    }
  };
  const initialLoading = loading && !data?.applications?.length;

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Advisor Applications</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "24px",
        }}
      >
        <SearchBar
          initialQuery={query}
          fullWidth={isMobile}
          autofocus={true}
          onQuery={setQuery}
          onKeyPress={true}
          sx={{ marginLeft: "0" }}
        />
        <ButtonGroup
          style={{
            display: "flex",
            ...(isMobile && {
              marginBottom: "16px",
              width: "100%",
            }),
          }}
        >
          <Button
            sx={{ flex: "1", width: "100%" }}
            variant={
              mainFilter ===
              ListAdvisorApplicationsRequest_ApplicationFilter.NEW
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setMainFilter(
                ListAdvisorApplicationsRequest_ApplicationFilter.NEW,
              )
            }
          >
            New
          </Button>
          <Button
            sx={{
              flex: "1",
              width: "125px",
              minWidth: "125px !important",
            }}
            variant={
              mainFilter ===
              ListAdvisorApplicationsRequest_ApplicationFilter.IN_PROGRESS
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setMainFilter(
                ListAdvisorApplicationsRequest_ApplicationFilter.IN_PROGRESS,
              )
            }
          >
            In Process
          </Button>
          <Button
            sx={{
              flex: "1",
              width: "80px",
              minWidth: "unset",
            }}
            variant={
              mainFilter ===
              ListAdvisorApplicationsRequest_ApplicationFilter.ALL
                ? "buttonGroupSelected"
                : "buttonGroup"
            }
            onClick={() =>
              setMainFilter(
                ListAdvisorApplicationsRequest_ApplicationFilter.ALL,
              )
            }
          >
            All
          </Button>
        </ButtonGroup>
      </Box>

      {/* Table contents */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
        }}
      >
        {!isMobile && (
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{
              padding: isMobile ? "0" : "24px 32px 0 32px",
              marginRight: "34px",
              gap: "24px",
            }}
          >
            <HeaderColumn
              title="Applicant"
              sortKey="firstName"
              sorts={sorts}
              setSorts={setSorts}
              flex={3}
            />
            <HeaderColumn
              title="Status"
              sortKey="status"
              sorts={sorts}
              setSorts={setSorts}
              flex={3}
            />
            <HeaderColumn
              title="Last Activity"
              sortKey="lastActivitySec"
              sorts={sorts}
              setSorts={setSorts}
              flex={2}
              end={true}
            />
          </Box>
        )}
        {initialLoading && <Loading />}
        {!data?.applications?.length && !loading && (
          <Box>
            <Alert severity="info">No applications found</Alert>
          </Box>
        )}
        {!initialLoading && appCards()}
      </Box>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={3000}
        message="CSV Downloaded"
      />
    </Box>
  );
};
